import { useCallback, useEffect, useRef, useState } from "react";
import {
  applyMove, checkEnd, createBoard, legalMoves, nextTurn, score,
  type Board, type Disc, type Outcome, opponent,
} from "./othello";
import { chooseMove } from "./ai";
import type { Stage } from "./stages";

export interface MatchView {
  board: Board;
  turn: Disc | null;
  playerDisc: Disc;
  aiDisc: Disc;
  moveNo: number;
  legal: number[];
  lastFlipped: number[];
  lastPlaced: number | null;
  passBy: Disc | null;
  ended: boolean;
  outcome: Outcome | null;
  playerCount: number;
  aiCount: number;
  emptyCount: number;
  canPlay: boolean;
}

interface Options {
  reducedMotion: boolean;
  onEnd: (outcome: Outcome, playerCount: number) => void;
  /** Halt the AI turn timer while true (pause menu open). */
  paused?: boolean;
}

const AI_DELAY_MS = 620;

export function useMatch(stage: Stage, opts: Options) {
  const playerDisc = stage.playerDisc;
  const aiDisc = opponent(playerDisc);

  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  // Mutable match state held in refs; React state is only a redraw trigger.
  const boardRef = useRef<Board>(createBoard());
  const turnRef = useRef<Disc | null>("dark"); // dark always starts
  const moveNoRef = useRef(1);
  const lastFlippedRef = useRef<number[]>([]);
  const lastPlacedRef = useRef<number | null>(null);
  const passByRef = useRef<Disc | null>(null);
  const endedRef = useRef(false);
  const outcomeRef = useRef<Outcome | null>(null);

  const timerRef = useRef<number>();
  const onEndRef = useRef(opts.onEnd);
  onEndRef.current = opts.onEnd;

  const clearTimer = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
  };

  const commitMove = useCallback(
    (move: number, who: Disc) => {
      const { board: next, flipped } = applyMove(boardRef.current, move, who);
      boardRef.current = next;
      lastPlacedRef.current = move;
      lastFlippedRef.current = flipped;
      moveNoRef.current += 1;

      const follow = nextTurn(next, who);
      passByRef.current = follow === who ? opponent(who) : null;

      const end = checkEnd(next);
      if (end.ended || follow === null) {
        endedRef.current = true;
        turnRef.current = null;
        const s = score(next);
        const decided: Outcome =
          end.outcome ??
          (s.dark === s.light ? "draw" : s.dark > s.light ? "dark" : "light");
        outcomeRef.current = decided;
        rerender();
        onEndRef.current(decided, playerDisc === "dark" ? s.dark : s.light);
        return;
      }
      turnRef.current = follow;
      rerender();
    },
    [playerDisc],
  );

  // Run the AI turn.
  useEffect(() => {
    if (opts.paused || endedRef.current || turnRef.current !== aiDisc) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      if (endedRef.current || turnRef.current !== aiDisc) return;
      const moves = legalMoves(boardRef.current, aiDisc);
      if (moves.length === 0) return;
      commitMove(chooseMove(stage.aiLevel, boardRef.current, aiDisc), aiDisc);
    }, opts.reducedMotion ? 180 : AI_DELAY_MS);
    return clearTimer;
  });

  useEffect(() => () => clearTimer(), []);

  const play = useCallback(
    (cell: number) => {
      if (endedRef.current || turnRef.current !== playerDisc) return;
      if (!legalMoves(boardRef.current, playerDisc).includes(cell)) return;
      commitMove(cell, playerDisc);
    },
    [playerDisc, commitMove],
  );

  // TEST build: force the match to a chosen end state (loss / draw / win) so
  // those result screens can be checked without playing them out.
  const finish = useCallback(
    (outcome: Outcome) => {
      if (endedRef.current) return;
      clearTimer();
      endedRef.current = true;
      turnRef.current = null;
      outcomeRef.current = outcome;
      rerender();
      const sc = score(boardRef.current);
      onEndRef.current(outcome, playerDisc === "dark" ? sc.dark : sc.light);
    },
    [playerDisc],
  );

  const restart = useCallback(() => {
    clearTimer();
    boardRef.current = createBoard();
    turnRef.current = "dark";
    moveNoRef.current = 1;
    lastFlippedRef.current = [];
    lastPlacedRef.current = null;
    passByRef.current = null;
    endedRef.current = false;
    outcomeRef.current = null;
    rerender();
  }, []);

  const s = score(boardRef.current);
  const turn = turnRef.current;
  const view: MatchView = {
    board: boardRef.current,
    turn,
    playerDisc,
    aiDisc,
    moveNo: moveNoRef.current,
    legal: turn === playerDisc ? legalMoves(boardRef.current, playerDisc) : [],
    lastFlipped: lastFlippedRef.current,
    lastPlaced: lastPlacedRef.current,
    passBy: passByRef.current,
    ended: endedRef.current,
    outcome: outcomeRef.current,
    playerCount: playerDisc === "dark" ? s.dark : s.light,
    aiCount: aiDisc === "dark" ? s.dark : s.light,
    emptyCount: s.empty,
    canPlay: !endedRef.current && turn === playerDisc,
  };

  return { view, play, restart, finish };
}
